import React, { FC, useCallback } from 'react';
import { getUserLang, isEuEnv } from '@mb/lib/i18n';
import { useInView } from 'react-intersection-observer';
import { useIntl } from 'react-intl';
import * as Styled from './ListView.styles';
import { formatImageSource } from '../../../../../../../../MIT/Search/view/frontend/pages/ProductSearch/helpers';
import { CURATED_COLLECTIONS_PAGE } from '../../constants';
import {
  clearAnalyticsEvents,
  sendSelectPromotionAnalyticsEvent,
  pluralize,
} from '../../helpers';
import { definedTranslation } from '../../i18n';
import { collectionItemType } from '../../types';

interface ListViewItemProps {
  campaign: collectionItemType;
  collectionTitle: string;
  index: number;
  sendInViewChange: (campaign: collectionItemType, visible: boolean) => void;
}

const CATEGORY_LIMIT = 3;
const TAG_LIMIT = 8;

const storageLang = getUserLang();
const prefix = isEuEnv ? `/${storageLang}` : '';

export const ListViewItem: FC<ListViewItemProps> = ({
  campaign,
  collectionTitle,
  sendInViewChange,
  index,
}) => {
  const imageSource = formatImageSource(campaign.thumbnailImageUrl, 120, false);
  const materialsCount = campaign.children_count ?? 0;
  const link =
    `${prefix}/${CURATED_COLLECTIONS_PAGE}/${campaign.url_key}` || '#';
  const title = campaign.name;
  const categories = campaign.categories || [];
  const { ref: inViewRef } = useInView({
    threshold: 0.5,
    onChange: onInViewChange,
  });

  const intl = useIntl();
  const materialText = intl?.formatMessage(
    definedTranslation.collections.materialText,
  );
  const materialsText = intl?.formatMessage(
    definedTranslation.collections.materialsText,
  );

  function onInViewChange(isInView: boolean) {
    sendInViewChange(campaign, isInView);
  }

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      clearAnalyticsEvents({ ecommerce: null }).then(() => {
        sendSelectPromotionAnalyticsEvent(collectionTitle, {
          name: title,
          index,
        });
      });
      window.location.href = link;
    },
    [link],
  );

  return (
    // @ts-expect-error // TODO: fix types
    <Styled.Item href={link} ref={inViewRef} onClick={handleClick}>
      <Styled.ItemData>
        <Styled.ItemCover>
          {campaign.thumbnailImageUrl && (
            <img
              src={imageSource.src}
              srcSet={imageSource.srcSet}
              alt={title}
            />
          )}
        </Styled.ItemCover>
        <Styled.TextWrapper>
          <Styled.ItemTitleWrapper>
            <Styled.ItemTitle>{title}</Styled.ItemTitle>
          </Styled.ItemTitleWrapper>
          <Styled.ItemInfoWrapper>
            <Styled.ItemInfo>
              {`${materialsCount} ${pluralize(
                materialsCount,
                materialText,
                materialsText,
              )}`}
            </Styled.ItemInfo>
          </Styled.ItemInfoWrapper>
        </Styled.TextWrapper>
      </Styled.ItemData>
      <Styled.Tags>
        {categories?.slice(0, CATEGORY_LIMIT).map((category) => (
          <Styled.Tag key={category}>
            {category.length > TAG_LIMIT && (
              <Styled.Tooltip align="right" style={{ marginTop: '8px' }}>
                {category}
              </Styled.Tooltip>
            )}
            <Styled.TagText variant="tiny">{category}</Styled.TagText>
          </Styled.Tag>
        ))}
        {categories?.length > CATEGORY_LIMIT && (
          <Styled.Tag>
            <Styled.TagText variant="tiny">
              +{campaign.categories.length - CATEGORY_LIMIT}
            </Styled.TagText>
          </Styled.Tag>
        )}
      </Styled.Tags>
    </Styled.Item>
  );
};
